import * as Dialog from '@radix-ui/react-dialog'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import CloseIcon from '@/assets/icons/close.svg?react'

import styles from './Modal.module.scss'
import ButtonIcon from '../ButtonIcon/ButtonIcon'
import Divider from '../Divider/Divider'

export type ModalSize = 'xs' | 'sm' | 'md' | 'lg'

type ModalProps = {
  id: string
  title: string
  open?: boolean
  size?: ModalSize
  contentClassName?: string
  boxClassName?: string
  trigger?: React.ReactNode
  children?: React.ReactNode
  description?: React.ReactNode
  onOpenChange?: (isOpen: boolean) => void
  onClose?: () => void
  footer?: React.ReactNode
  hideCloseIcon?: boolean
  fullScreenOnTablet?: boolean
}

const Modal = (props: ModalProps) => (
  <BaseModal
    trigger={props.trigger}
    open={props.open}
    description={props.description}
    contentClassName={props.contentClassName}
    title={props.title}
    size={props.size}
    onClose={props.onClose}
    onOpenChange={props.onOpenChange}
    hideCloseIcon={props.hideCloseIcon}
  >
    <div className={clsx(styles.box, props.boxClassName)}>{props.children}</div>

    {props.footer ? <div className={styles.footer}> {props.footer}</div> : null}
  </BaseModal>
)

type BaseModalProps = {
  title: string
  open?: boolean
  size?: ModalSize
  contentClassName?: string
  hideCloseIcon?: boolean
  trigger?: React.ReactNode
  children?: React.ReactNode
  description?: React.ReactNode
  onOpenChange?: (isOpen: boolean) => void
  onClose?: () => void
  fullscreenOnTablet?: boolean
}

export const BaseModal = (props: BaseModalProps) => {
  const { size = 'sm', hideCloseIcon = false } = props
  const { t } = useTranslation('common')

  return (
    <Dialog.Root
      open={props.open}
      onOpenChange={value => {
        props.onOpenChange?.(value)
      }}
    >
      <Dialog.Trigger asChild>{props.trigger}</Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay>
          <div className={styles.overlay} />
          <Dialog.Content
            tabIndex={undefined}
            aria-modal="true"
            className={clsx(
              styles.content,
              styles[size],
              props.contentClassName,
              props.fullscreenOnTablet && styles.fullScreenOnTablet
            )}
            onInteractOutside={event => {
              // prevent the modal from closing and showing the dirty modal
              // when the focus moves to the buttons from the confirm modal to remove item
              if (event.detail.originalEvent.type === 'focusin') {
                event.preventDefault()
                return
              }

              if (props.onClose) {
                event?.preventDefault()
                props.onClose()
              }
            }}
            onEscapeKeyDown={event => {
              if (props.onClose) {
                event.preventDefault()
                props.onClose()
              }
            }}
            {...(props.description ? {} : { 'aria-describedby': undefined })}
          >
            <div className={styles.titleWrapper}>
              <Dialog.Title className={styles.title}>
                {props.title}
              </Dialog.Title>

              {props.description ? (
                <Dialog.Description className={styles.description}>
                  {props.description}
                </Dialog.Description>
              ) : null}
            </div>

            <Divider className={styles.divider} />
            {props.children}

            {!hideCloseIcon ? (
              <Dialog.Close asChild>
                <ButtonIcon
                  className={styles.closeIcon}
                  size="small"
                  ariaLabel={t('button.close')}
                  variant="tertiary"
                  onClick={event => {
                    if (props.onClose) {
                      event?.preventDefault()
                      props.onClose()
                    }
                  }}
                >
                  <CloseIcon />
                </ButtonIcon>
              </Dialog.Close>
            ) : null}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default Modal
